import { useState, type FC } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import Stack from "@mui/material/Stack";
import ListItemButton from "@mui/material/ListItemButton";
import { Collapse } from "@mui/material";
import { ChevronDownIcon, ChevronRightIcon } from "../../../shared/icons";
import type { FormSection, FormSectionGroup, TemplateFormSettings } from "../../../api/templateFormSettings.types";
import { logError } from "../../../api/logError";

type DialogMenuProps = {
  formSettings: TemplateFormSettings;
  selectedSectionId: string;
  onSectionSelect: (id: string) => void;
};

export const TemplateDialogMenu: FC<DialogMenuProps> = ({ formSettings, selectedSectionId, onSectionSelect }) => {
  return (
    <Box
      sx={{
        pt: 1,
        width: "17rem",
        bgcolor: theme => theme.palette.grey[100],
        borderRight: "1px solid",
        borderRightColor: theme => theme.palette.divider,
      }}
    >
      <List sx={{ px: 1 }}>
        {formSettings.sectionGroups ? (
          <SectionGroupListItems
            formSectionGroupWithSections={mapSectionsToSectionGroupsWithSections(
              formSettings.sectionGroups,
              formSettings.sections
            )}
            sections={formSettings.sections}
            onSectionSelect={onSectionSelect}
            selectedSectionId={selectedSectionId}
          />
        ) : (
          formSettings.sections.map(({ id: sectionId, name, totalNumberOfQuestions }) => (
            <ListItem
              key={sectionId}
              sectionId={sectionId}
              name={name}
              totalNumberOfQuestions={totalNumberOfQuestions}
              onSectionSelect={onSectionSelect}
              selected={
                selectedSectionId
                  ? window.location.pathname.includes(sectionId)
                  : sectionId === formSettings.sections[0].id
              }
            />
          ))
        )}
      </List>
    </Box>
  );
};

const SectionGroupListItems: React.FC<{
  formSectionGroupWithSections: FormSectionGroupWithSections[];
  sections: FormSection[];
  onSectionSelect: (id: string) => void;
  selectedSectionId: string;
}> = ({ formSectionGroupWithSections, sections, onSectionSelect, selectedSectionId }) => {
  return formSectionGroupWithSections.map(({ sections: sectionsInGroup, id, name }) => {
    if (sectionsInGroup.length === 0) throw new Error(`A section group(${id}) must have at least one section.`);
    if (sectionsInGroup.length === 1) {
      const [section] = sectionsInGroup;
      return (
        <ListItem
          key={section.id}
          sectionId={section.id}
          name={section.name}
          totalNumberOfQuestions={section.totalNumberOfQuestions}
          onSectionSelect={onSectionSelect}
          selected={selectedSectionId ? window.location.pathname.includes(section.id) : section.id === sections[0].id}
        />
      );
    }
    return (
      <SectionGroupListItem
        id={id}
        name={name}
        sections={sectionsInGroup}
        key={id}
        onSectionSelect={onSectionSelect}
        selectedSectionId={selectedSectionId}
      />
    );
  });
};

const SectionGroupListItem: React.FC<{
  id: string;
  name: string;
  sections: FormSection[];
  onSectionSelect: (id: string) => void;
  selectedSectionId: string;
}> = ({ id, name, sections, onSectionSelect, selectedSectionId }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isSelected = selectedSectionId ? window.location.pathname.includes(id) : id === sections[0].id;
  return (
    <Stack key={id}>
      <ListItemButton
        onClick={() => setIsExpanded(!isExpanded)}
        sx={{
          "px": 4,
          "py": 1.5,
          "cursor": "pointer",
          "&:hover": { bgcolor: theme => theme.palette.grey["200"] },
          "&& .Mui-selected ": { bgcolor: theme => theme.palette.grey["300"], fontWeight: "600" },
          "&& .Mui-selected > div > p": { fontWeight: "600" },
        }}
        selected={isSelected}
      >
        <Stack
          spacing={4}
          direction="row"
          sx={{
            "display": "flex",
            "width": "100%",
            "justifyContent": "space-between",
            "alignItems": "center",
            "&&.Mui-selected": { bgcolor: theme => theme.palette.grey["300"], fontWeight: "600" },
          }}
        >
          <Typography variant="body2" sx={{ lineHeight: 1.2 }}>
            {name}
          </Typography>
          {isExpanded ? (
            <ChevronDownIcon color={theme => theme.palette.text.secondary} size={14} />
          ) : (
            <ChevronRightIcon color={theme => theme.palette.text.secondary} size={14} />
          )}
        </Stack>
      </ListItemButton>
      <Collapse in={isExpanded}>
        {sections.map(({ id: sectionId, name, totalNumberOfQuestions }) => (
          <ListItem
            key={sectionId}
            sectionId={sectionId}
            name={name}
            totalNumberOfQuestions={totalNumberOfQuestions}
            onSectionSelect={onSectionSelect}
            selected={selectedSectionId ? window.location.pathname.includes(sectionId) : sectionId === sections[0].id}
            subItem
          />
        ))}
      </Collapse>
    </Stack>
  );
};

const ListItem: React.FC<{
  sectionId: string;
  name: string;
  totalNumberOfQuestions: number;
  onSectionSelect: (id: string) => void;
  selected: boolean;
  subItem?: boolean;
}> = ({ sectionId, name, totalNumberOfQuestions, onSectionSelect, selected, subItem }) => {
  return (
    <ListItemButton
      key={sectionId}
      onClick={() => onSectionSelect(sectionId)}
      sx={{
        "my": 0.5,
        "px": 4,
        "py": 1.2,
        "cursor": "pointer",
        "borderRadius": 1,
        "&:hover": { backgroundColor: theme => theme.palette.grey["200"] },
        "&&.Mui-selected": { backgroundColor: theme => theme.palette.grey["300"], fontWeight: "600" },
        "&&.Mui-selected > div > p": { fontWeight: "600" },
        ...(subItem && { pl: 8, pr: 4 }),
      }}
      selected={selected}
    >
      <Stack
        spacing={4}
        direction="row"
        sx={{
          "display": "flex",
          "width": "100%",
          "justifyContent": "space-between",
          "alignItems": "center",
          "&&.Mui-selected": { bgcolor: theme => theme.palette.grey["300"], fontWeight: "600" },
        }}
      >
        <Typography
          sx={{
            lineHeight: 1.2,
            fontSize: theme => theme.typography.pxToRem(14),
            color: theme => (subItem || selected ? theme.palette.text.primary : theme.palette.text.tertiary),
          }}
        >
          {name}
        </Typography>
        <Typography
          sx={{ color: theme => theme.palette.text.tertiary, fontSize: theme => theme.typography.pxToRem(12) }}
        >
          {totalNumberOfQuestions}
        </Typography>
      </Stack>
    </ListItemButton>
  );
};

type FormSectionGroupWithSections = FormSectionGroup & { sections: FormSection[] };
const mapSectionsToSectionGroupsWithSections = (sectionGroups: FormSectionGroup[], sections: FormSection[]) => {
  const formSectionGroupWithSections = new Map<FormSectionGroup["id"], FormSectionGroupWithSections>();
  sectionGroups.forEach(group =>
    formSectionGroupWithSections.set(group.id, { name: group.name, id: group.id, sections: [] })
  );
  sections.forEach(section => {
    if (!section.sectionGroupId) return logError(`"sectionGroupId" is required when form has sectionGroups`);
    const group = formSectionGroupWithSections.get(section.sectionGroupId);
    if (group) group.sections.push(section);
    else logError(`Section "${section.name}" does not match any group.`);
  });
  return Array.from(formSectionGroupWithSections.values());
};
